import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { CheckCircle, FileText, ArrowRight, Stethoscope } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const List = ({ items, testid }) => (
  <ul className="space-y-2" data-testid={testid}>
    {items.map((x) => (
      <li key={x} className="flex gap-2 text-secondary text-sm sm:text-base">
        <CheckCircle size={18} weight="fill" className="text-primary flex-shrink-0 mt-0.5" /> {x}
      </li>
    ))}
  </ul>
);

export default function SpecialtyPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/seo/specialties/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <Navigate to="/specialties" replace />;

  return (
    <div className="min-h-screen bg-surface" data-testid="specialty-page">
      <Navbar />
      <main className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <nav className="text-xs text-secondary mb-6" data-testid="specialty-breadcrumb">
          <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/specialties" className="hover:text-primary">Specialties</Link> / <span>{data?.name || '…'}</span>
        </nav>
        {!data ? <p className="text-secondary">Loading…</p> : (
          <>
            <h1 className="font-spectral text-4xl sm:text-5xl font-bold text-primary leading-tight mb-5" data-testid="specialty-title">
              Second opinion in {data.name}
            </h1>
            <p className="text-base sm:text-lg text-secondary leading-relaxed mb-10" data-testid="specialty-summary">{data.summary}</p>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <section className="bg-surface-secondary rounded-2xl p-6 border border-primary/10">
                <h2 className="font-semibold text-primary text-lg mb-4">What gets reviewed</h2>
                <List items={data.reviewed} testid="specialty-reviewed" />
              </section>
              <section className="bg-surface-secondary rounded-2xl p-6 border border-primary/10">
                <h2 className="font-semibold text-primary text-lg mb-4 flex items-center gap-2"><FileText size={20} /> Which reports to upload</h2>
                <List items={data.reports} testid="specialty-reports" />
              </section>
            </div>

            {data.subspecialties.length > 0 && (
              <section className="mb-10">
                <h2 className="font-semibold text-primary text-lg mb-3">Subspecialties</h2>
                <div className="flex flex-wrap gap-2" data-testid="specialty-subspecialties">
                  {data.subspecialties.map((s) => <span key={s} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">{s}</span>)}
                </div>
              </section>
            )}

            <section className="mb-10">
              <h2 className="font-semibold text-primary text-lg mb-3 flex items-center gap-2"><Stethoscope size={20} /> Who reviews your case</h2>
              <p className="text-secondary mb-3">Specialists with a postgraduate qualification, registered with the NMC or a State Medical Council. Registration numbers are checked before a doctor can review cases.</p>
              {data.doctors.length > 0 && (
                <ul className="flex flex-wrap gap-3" data-testid="specialty-doctors">
                  {data.doctors.map((d) => <li key={d.id}><Link to={`/dr/${d.id}`} className="underline text-primary">{d.name}</Link></li>)}
                </ul>
              )}
            </section>

            <section className="mb-10" data-testid="specialty-faq">
              <h2 className="font-semibold text-primary text-lg mb-4">Questions patients ask</h2>
              {data.faqs.map((f) => (
                <div key={f.q} className="mb-5">
                  <h3 className="font-medium text-primary">{f.q}</h3>
                  <p className="text-secondary text-sm sm:text-base mt-1">{f.a}</p>
                </div>
              ))}
            </section>

            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="specialty-cta">Start your consultation <ArrowRight size={16} /></Link>
              <Link to="/blog" className="px-6 py-3 border-2 border-primary text-primary rounded-full text-sm font-medium" data-testid="specialty-blog-link">Patient guides</Link>
              <Link to="/specialties" className="px-6 py-3 text-primary text-sm underline" data-testid="specialty-all-link">All specialties</Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
